import {productoActions} from "../../../../_actions";

/**
 * Qué significa cada estado de una venta, en un solo lugar.
 *
 * <p>El orden del array ES el orden del flujo real —pendiente de pago → pagada → enviada →
 * finalizada, con cancelada al final— y lo usan tanto las tarjetas del resumen como las solapas,
 * para que el vendedor vea siempre la misma secuencia y en la misma posición.
 *
 * <p>`ayuda` existe porque el nombre del estado no alcanza: "Pagada" y "Pendiente de entrega" suenan
 * parecido y lo que las diferencia es de quién es el próximo paso. La frase dice exactamente eso.
 */
export const ESTADOS_VENTA = [
    {
        key: "PENDIENTE_PAGO",
        titulo: "Pendientes de pago",
        corto: "A cobrar",
        icono: "fa-solid fa-hourglass-half",
        tono: "espera",
        ayuda: "Todavía no cobraste. Cuando confirmes el pago, entrá al detalle y marcalas como Pagadas.",
    },
    {
        key: "PAGADA",
        titulo: "Pagadas",
        corto: "A preparar",
        icono: "fa-solid fa-circle-check",
        tono: "cobrado",
        ayuda: "Ya cobraste. Falta preparar el repuesto y marcarlo como Enviado.",
    },
    {
        key: "PENDIENTE_ENTREGA",
        titulo: "Pendientes de entrega",
        corto: "En camino",
        icono: "fa-solid fa-truck-fast",
        tono: "camino",
        ayuda: "Enviadas o listas para retirar. Cuando el comprador las reciba, marcalas como Finalizadas.",
    },
    {
        key: "FINALIZADA",
        titulo: "Finalizadas",
        corto: "Cerradas",
        icono: "fa-solid fa-flag-checkered",
        tono: "listo",
        ayuda: "Entregadas y cerradas: no queda nada pendiente de tu lado.",
    },
    {
        key: "CANCELADA",
        titulo: "Canceladas",
        corto: "Canceladas",
        icono: "fa-solid fa-ban",
        tono: "baja",
        ayuda: "Ventas que no se concretaron. El stock volvió a tus publicaciones.",
    },
];

/**
 * El estado del flujo que le corresponde a una venta.
 *
 * <p>Une `venta.estado` (el string que viene del backend) con la entrada de
 * {@link ESTADOS_VENTA}, que es donde viven el icono, el tono y la frase de ayuda. Asi el
 * detalle pinta el estado con el mismo color que la tarjeta del resumen y la solapa, sin
 * repetir el mapeo en cada pantalla.
 *
 * <p>Devuelve `undefined` para un estado que no conocemos: quien lo llama tiene que poder
 * dibujar el detalle igual, sin color y sin ayuda, en vez de romperse.
 */
export const estadoDeVenta = (estado) =>
    ESTADOS_VENTA.find((it) => it.key === estado);

/**
 * WhatsApp al comprador con la venta ya mencionada.
 *
 * <p>Devuelve `undefined` si no hay número, y el botón se esconde: un link a `wa.me/+549undefined`
 * abre WhatsApp igual y el vendedor se entera recién cuando está mirando un chat vacío.
 */
export const linkWhatsappComprador = (venta) => {
    const telefono = productoActions.normalizarTelefono(venta?.comprador?.numeroCelular);

    if (!telefono || telefono === "+549") return undefined;

    const texto = encodeURIComponent(
        `Hola! Te escribo por tu compra #${venta.id} en DeAutopartes.`
    );

    return `https://api.whatsapp.com/send?phone=${telefono}&text=${texto}`;
};

/**
 * WhatsApp al vendedor con la venta ya mencionada — el equivalente de {@link linkWhatsappComprador}
 * para la lista de "Mis compras".
 */
export const linkWhatsappVendedor = (venta) => {
    const telefono = productoActions.normalizarTelefono(venta?.vendedor?.numeroCelular);

    if (!telefono || telefono === "+549") return undefined;

    const texto = encodeURIComponent(
        `Hola! Te escribo por mi compra #${venta.id} en DeAutopartes.`
    );

    return `https://api.whatsapp.com/send?phone=${telefono}&text=${texto}`;
};
